import { useDrag } from "@use-gesture/react";
import classNames from "classnames";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Transition,
  TransitionGroup,
  TransitionStatus,
} from "react-transition-group";
import {
  ENTERED,
  ENTERING,
  EXITED,
  EXITING,
} from "react-transition-group/Transition";
import { logEvent } from "../Amplitude";
import { uiStateMobx } from "../SharedIframeState";
import { ActionType, PaneType, Position } from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { CanvasDispatchEmitter } from "./CanvasDispatch";
import { canvasAtom } from "./CanvasState";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import tutorialPaneToolImage from "./tutorial_pane_tool.png";

export function showTutorial(tutorialId: string) {
  ParentComlinkAPI.navigateCanvas(["tutorial", tutorialId]);
}

type TutorialAPI = {
  prevStep: () => void;
  nextStep: () => void;
  animatePaneTo: (position: Position) => void;
  getExpression: (paneId: string) => string | undefined;
};

type TutorialStepProps = {
  api: TutorialAPI;
};

type Tutorial = {
  id: string;
  name: string;
  steps: React.ComponentType<TutorialStepProps>[];
};

function TutorialStepAnimator({
  direction,
  state,
  children,
}: {
  direction: "forward" | "backward";
  state: TransitionStatus;
  children: React.ReactNode;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (state === EXITED || state === EXITING) {
      rootRef.current!.scrollTop;
    }
  }, [state]);
  return (
    <div
      className={classNames(
        "transition transform",
        state === EXITING ? "absolute top-0 left-0 right-0" : undefined,
        state === ENTERING || state === ENTERED ? "opacity-100" : "opacity-0",
        {
          "translate-x-4":
            direction === "forward" && (state === EXITED || state === EXITING),
          "-translate-x-4":
            direction === "backward" && (state === EXITING || state === EXITED),
        }
      )}
      ref={rootRef}
    >
      {children}
    </div>
  );
}

function TutorialParagraph({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={classNames("mb-4", className)}>{children}</div>;
}

function TutorialContinueButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="font-medium bg-primary-50 text-primary-500 hover:bg-primary-500 hover:text-neutral-0 rounded px-3 py-2 transition"
    >
      {children}
    </button>
  );
}

function useAdvanceOnMutatorEvent(api: TutorialAPI, actionType: ActionType) {
  useEffect(() => {
    function onEvent() {
      api.nextStep();
    }
    CanvasDispatchEmitter.addListener(`${actionType}`, onEvent);
    return () => {
      CanvasDispatchEmitter.removeListener(`${actionType}`, onEvent);
    };
  }, []);
}

const tutorials: Record<string, Tutorial> = {
  "tip-calculator": {
    id: "tip-calculator",
    name: "intro to natto",
    steps: [
      function ({ api }: TutorialStepProps) {
        return (
          <div className="p-4">
            <TutorialParagraph className="bg-secondary-50 rounded p-3">
              The patrons of our restaurant, Bar Natto, have been{" "}
              <strong>undertipping</strong> and summing up their totals
              incorrectly. Let's fix this once and for all by building a{" "}
              <strong>tip calculator</strong>!
            </TutorialParagraph>
            <TutorialParagraph>
              This tutorial will introduce you to the basics of natto.
            </TutorialParagraph>
            <TutorialParagraph>
              Performing the{" "}
              <span className="font-medium text-primary-500">blue action</span>{" "}
              will advance the tutorial. Ready?
            </TutorialParagraph>
            <TutorialContinueButton onClick={() => api.nextStep()}>
              let's go!
            </TutorialContinueButton>
          </div>
        );
      },
      function ({ api }: TutorialStepProps) {
        useAdvanceOnMutatorEvent(api, ActionType.CreatePane);
        return (
          <div className="p-4">
            <TutorialParagraph>
              Let's start by making an <strong>eval pane</strong>.{" "}
              <span className="font-medium text-primary-500">
                Select the eval pane tool
              </span>{" "}
              in the toolbar.
            </TutorialParagraph>
            <img
              src={tutorialPaneToolImage}
              className="w-64 h-24 mb-4 rounded"
              alt="screenshot of the eval pane tool in toolbar"
            />
            <TutorialParagraph>
              Then,{" "}
              <span className="font-medium text-primary-500">
                drag a rectangle
              </span>{" "}
              on the canvas.
            </TutorialParagraph>
          </div>
        );
      },
      function ({ api }: TutorialStepProps) {
        useEffect(() => {
          function onEvent({ paneId }: { paneId: string }) {
            const expression = api.getExpression(paneId)!;
            if (expression.trim() === "64") {
              api.nextStep();
            }
          }
          CanvasDispatchEmitter.addListener(
            `${ActionType.UpdatePaneCodemirror}`,
            onEvent
          );
          return () => {
            CanvasDispatchEmitter.removeListener(
              `${ActionType.UpdatePaneCodemirror}`,
              onEvent
            );
          };
        }, []);
        return (
          <div className="p-4">
            <TutorialParagraph>
              <strong>Eval panes</strong> are the core building block of natto.
              Each one has a resizable editor and an output area. Try typing a
              JavaScript expression!
            </TutorialParagraph>
            <ul className="list-disc pl-6 mb-4">
              <li>
                <code>1 + 2</code>
              </li>
              <li>
                <code>new Date()</code>
              </li>
              <li>
                <code>{`[{a:1,b:2}, {c:3,d:4}]`}</code>
              </li>
            </ul>
            <TutorialParagraph>
              Because this pane has <strong>autorun</strong> enabled, it will
              run when its code changes.
            </TutorialParagraph>
            <div>
              When you're ready,{" "}
              <span className="font-medium text-primary-500">
                change the pane's expression to 64
              </span>
            </div>
          </div>
        );
      },
      function ({ api }: TutorialStepProps) {
        useAdvanceOnMutatorEvent(api, ActionType.CreateTransformPane);
        return (
          <div className="p-4">
            <TutorialParagraph>
              See the arrow in the top-right corner of the pane? It represents
              the pane's output.
            </TutorialParagraph>
            <div className="font-medium text-primary-500">
              Drag the arrow onto an empty spot on the canvas
            </div>
          </div>
        );
      },
      function ({ api }: TutorialStepProps) {
        useEffect(() => {
          function onEvent({ paneId }: { paneId: string }) {
            const expression = api.getExpression(paneId)!;
            if (expression.replace(/[ 0]/g, "") === "value*.19") {
              api.nextStep();
            }
          }
          CanvasDispatchEmitter.addListener(
            `${ActionType.UpdatePaneCodemirror}`,
            onEvent
          );
          return () => {
            CanvasDispatchEmitter.removeListener(
              `${ActionType.UpdatePaneCodemirror}`,
              onEvent
            );
          };
        }, []);
        return (
          <div className="p-4">
            <TutorialParagraph>
              You just created a new <strong>eval pane</strong> with an input!
            </TutorialParagraph>
            <TutorialParagraph>
              The input is automatically named <strong>value</strong> and
              referenced as a variable in the code.
            </TutorialParagraph>
            <div className="font-medium text-primary-500">
              Change the new pane's expression to
            </div>
            <code className="block bg-primary-50 rounded p-2 my-2">
              value * 0.19
            </code>
          </div>
        );
      },
      function ({ api }: TutorialStepProps) {
        return (
          <div className="p-4">
            <TutorialParagraph>
              Try changing the number in the previous pane.
            </TutorialParagraph>
            <TutorialParagraph>
              Both panes update! If a pane has <strong>autorun</strong> enabled,
              it will also update whenever an input pane runs.
            </TutorialParagraph>
            <TutorialContinueButton onClick={() => api.nextStep()}>
              click to continue
            </TutorialContinueButton>
          </div>
        );
      },
      function ({ api }: TutorialStepProps) {
        useEffect(() => {
          function onEvent() {
            const panes = canvasAtom.value.panes;
            if (
              panes.some((p) => (p as any).name === "price") &&
              panes.some((p) => (p as any).name === "tip")
            ) {
              api.nextStep();
            }
          }
          CanvasDispatchEmitter.addListener(
            `${ActionType.SetPaneName}`,
            onEvent
          );
          return () => {
            CanvasDispatchEmitter.removeListener(
              `${ActionType.SetPaneName}`,
              onEvent
            );
          };
        }, []);
        return (
          <div className="p-4">
            <TutorialParagraph>
              You can name your panes to organize your canvas. In a pane's
              header, click on "name" to rename the pane.
            </TutorialParagraph>
            <div className="font-medium text-primary-500">
              Name the first pane <strong>price</strong> and the second pane{" "}
              <strong>tip</strong>
            </div>
          </div>
        );
      },
      function ({ api }: TutorialStepProps) {
        useEffect(() => {
          function onEvent({ expression }: any) {
            if (expression === "price") {
              api.nextStep();
            }
          }
          CanvasDispatchEmitter.addListener(
            `${ActionType.CreateTransformPane}`,
            onEvent
          );
          return () => {
            CanvasDispatchEmitter.removeListener(
              `${ActionType.CreateTransformPane}`,
              onEvent
            );
          };
        }, []);
        return (
          <div className="p-4">
            <TutorialParagraph>
              Let's create another pane.{" "}
              <span className="font-medium text-primary-500">
                Drag the output arrow
              </span>{" "}
              of the pane named <strong>price</strong> onto an empty area.
            </TutorialParagraph>
          </div>
        );
      },
      function ({ api }: TutorialStepProps) {
        useAdvanceOnMutatorEvent(api, ActionType.PushPaneInput);
        return (
          <div className="p-4">
            <TutorialParagraph>
              The input name defaults to the name of the connected pane. If the
              input pane has no name, it uses <strong>value</strong>.
            </TutorialParagraph>
            <TutorialParagraph>
              <span className="font-medium text-primary-500">
                Drag the output arrow
              </span>{" "}
              of the pane named <strong>tip</strong> on top of the new pane you
              just created.{" "}
              <span className="font-medium text-primary-500">
                Release your mouse
              </span>{" "}
              when the target pane has a blue outline.
            </TutorialParagraph>
          </div>
        );
      },
      function ({ api }: TutorialStepProps) {
        return (
          <div className="p-4">
            <div className="mb-2">Change the new pane's expression to</div>
            <code className="block bg-primary-50 rounded p-2 mb-4">
              price + tip
            </code>
            <TutorialParagraph>
              Nice! You just made a tip calculator on natto! Bar Natto customers
              shall undertip no more.
            </TutorialParagraph>
            <TutorialParagraph>
              I hope you enjoyed your little taste of natto. Look forward to
              more tutorials and I'd love to hear your feedback on{" "}
              <a
                href="https://twitter.com/_paulshen"
                target="_blank"
                className="underline"
                rel="noreferrer"
              >
                twitter
              </a>
              ! 🙏
            </TutorialParagraph>
            <div>
              <TutorialContinueButton
                onClick={() => {
                  ParentComlinkAPI.showCanvasSelector();
                }}
              >
                switch canvas
              </TutorialContinueButton>
            </div>
          </div>
        );
      },
    ],
  },
};

const TutorialPaneImpl = observer(({ tutorial }: { tutorial: Tutorial }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const tutorialStep = tutorial.steps[stepIndex];
  const hasMaximizedPane = computed(
    () => uiStateMobx.maximizedPaneId !== undefined
  ).get();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const offsetRef = useRef<Position>([0, 0]);

  useLayoutEffect(() => {
    offsetRef.current = [
      window.innerWidth - rootRef.current!.offsetWidth - 32,
      64,
    ];
    rootRef.current!.style.transform = `translate(${offsetRef.current[0]}px, ${offsetRef.current[1]}px)`;
    logEvent("view tutorial", { "tutorial id": tutorial.id });
  }, []);

  const bindDrag = useDrag(
    ({ target, first, last, offset }) => {
      offsetRef.current = offset;
      rootRef.current!.style.transform = `translate(${offset[0]}px, ${offset[1]}px)`;
      if (first && target instanceof HTMLElement) {
        target.style.cursor = "grabbing";
      }
      if (last && target instanceof HTMLElement) {
        target.style.cursor = "";
      }
    },
    { from: () => offsetRef.current }
  );

  const tutorialAPI = useMemo(
    () => ({
      canvasAtom,
      prevStep: () => {
        setStepIndex((stepIndex) => Math.max(0, stepIndex - 1));
      },
      nextStep: () => {
        setStepIndex((stepIndex) => {
          const nextStepIndex = Math.min(
            tutorial.steps.length - 1,
            stepIndex + 1
          );
          logEvent("advance tutorial", {
            "tutorial id": tutorial.id,
            "step index": nextStepIndex,
          });
          return nextStepIndex;
        });
      },
      animatePaneTo: (position: Position) => {
        const rootElement = rootRef.current!;
        rootElement.classList.add("transition");
        setTimeout(() => {
          rootElement.classList.remove("transition");
        }, 200);
        offsetRef.current = position;
        rootRef.current!.style.transform = `translate(${offsetRef.current[0]}px, ${offsetRef.current[1]}px)`;
      },
      getExpression: (paneId: string) => {
        const canvas = canvasAtom.value;
        const pane = canvas.panes.find((p) => p.id === paneId);
        if (pane?.type === PaneType.Evaluate) {
          return pane.expression;
        }
        return undefined;
      },
    }),
    [tutorial]
  );

  const prevStepIndexRef = useRef(stepIndex);
  useEffect(() => void (prevStepIndexRef.current = stepIndex));

  const [minimizePane, setMinimizePane] = useState(false);

  return (
    <div
      className={classNames(
        "bg-neutral-0 fixed top-0 left-0 w-96 max-w-[calc(100vw-3rem)] flex flex-col rounded overflow-hidden shadow-xl transform",
        hasMaximizedPane ? "-z-1" : "z-1"
      )}
      ref={rootRef}
    >
      <div
        {...bindDrag()}
        className="bg-neutral-800 text-neutral-0 h-8 px-2 flex items-center justify-between cursor-grab touch-none"
      >
        <div className="flex items-center">
          <button
            onClick={() => {
              ParentComlinkAPI.showCanvasSelector();
            }}
            className="flex mr-1"
          >
            <div className="flex items-center justify-center h-3 w-3 rounded-md hover:bg-red-400 transition-colors bg-neutral-500 text-neutral-0">
              <FeatherIcon icon="x" size="xs" />
            </div>
          </button>
          <button
            onClick={() => {
              setMinimizePane((v) => !v);
            }}
            className="flex mr-2"
          >
            <div className="flex items-center justify-center h-3 w-3 rounded-md hover:bg-yellow-400 transition-colors bg-neutral-500 text-neutral-0">
              <FeatherIcon icon="minus" size="xs" />
            </div>
          </button>
          <div>tutorial</div>
        </div>
        <div className="flex items-center">
          <div className="text-neutral-400 mr-1">
            {stepIndex + 1}/{tutorial.steps.length}
          </div>
          <button
            onClick={() => {
              setStepIndex((stepIndex) => Math.max(0, stepIndex - 1));
            }}
            disabled={stepIndex === 0}
            className="flex p-1 disabled:opacity-25 transition-opacity"
          >
            <FeatherIcon icon="arrow-left" size="sm" />
          </button>
          <button
            onClick={() => {
              setStepIndex((stepIndex) =>
                Math.min(tutorial.steps.length - 1, stepIndex + 1)
              );
            }}
            disabled={stepIndex >= tutorial.steps.length - 1}
            className="flex p-1 disabled:opacity-25 transition-opacity"
          >
            <FeatherIcon icon="arrow-right" size="sm" />
          </button>
        </div>
      </div>
      {!minimizePane ? (
        <div className="min-h-[8rem] relative overflow-hidden">
          <TransitionGroup>
            <Transition
              timeout={300}
              nodeRef={{ current: null }}
              key={stepIndex}
            >
              {(state) => (
                <TutorialStepAnimator
                  direction={
                    prevStepIndexRef.current < stepIndex
                      ? "forward"
                      : "backward"
                  }
                  state={state}
                >
                  {React.createElement(tutorialStep, {
                    api: tutorialAPI,
                  })}
                </TutorialStepAnimator>
              )}
            </Transition>
          </TransitionGroup>
        </div>
      ) : null}
    </div>
  );
});

export function TutorialPane({ tutorialId }: { tutorialId: string }) {
  const tutorial = tutorials[tutorialId];
  if (tutorial === undefined) {
    return null;
  }
  return <TutorialPaneImpl tutorial={tutorial} />;
}
